/**
 * Screen — borehole image workstation.
 *
 * One depth window, shown four ways at once because each answers a question the
 * others cannot: the static image for what the rock is, the dynamic image for
 * what it is doing, the cylinder for where it is going round the hole, and the
 * tadpole and fracture columns for how all of that stacks up with depth.
 *
 * The interpretation loop is: click three or more points along a feature on the
 * unrolled image, let the server fit the sinusoid, then save it as a plane of a
 * named kind. Everything downstream — the tadpoles, the roses, the stress
 * estimate and the published fracture curves — is that list of planes read
 * different ways.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { api, unpage, type Page } from "../api";
import { canCompute, canWrite } from "../auth";
import { count } from "../lib/plural";
import { AzimuthRuler, ImageCanvas, type PickPoint } from "../components/ImageCanvas";
import { BoreholeCylinder } from "../components/BoreholeCylinder";
import { DipScatter, RoseDiagram } from "../components/RoseDiagram";
import {
  IntervalColumn,
  SpikeTrack,
  TadpoleTrack,
  runningStats,
} from "../components/StructuralTracks";
import {
  Button,
  Dropdown,
  Empty,
  KeyValue,
  Loading,
  Notice,
  OptionalNumberField,
  Panel,
  Segmented,
  Slider,
  Toggle,
  useToast,
} from "../components/ui";
import { useAsync, useStored } from "../hooks";
import { rampGradient, rampKeys, rampLabel } from "../lib/colorscale";
import { type Breakout, type ByteStretch, type Feature, bearing, previewFit } from "../lib/imagelog";
import { EditColoursButton } from "../components/PaletteEditor";
import { WellDocuments, useWellDocuments } from "../components/WellDocuments";
import type {
  CoreDescription,
  DamageZone,
  ImageLog,
  ImageRun,
  ImageStatistics,
  Project,
  SinusoidFit,
  Well,
} from "../types";

type Rail = "pick" | "features" | "stats" | "stress" | "process" | "scans";

const KINDS: { value: string; label: string }[] = [
  { value: "bed", label: "Bed boundary" },
  { value: "cross_bed", label: "Cross bedding" },
  { value: "lamination", label: "Lamination" },
  { value: "cond_frac", label: "Conductive fracture" },
  { value: "res_frac", label: "Resistive fracture" },
  { value: "fissure", label: "Fissure" },
  { value: "induced_frac", label: "Drilling-induced fracture" },
  { value: "cond_fault", label: "Conductive fault" },
  { value: "res_fault", label: "Resistive fault" },
  { value: "vug", label: "Vug" },
  { value: "nodule", label: "Nodule" },
];

const FRACTURE_KINDS = ["cond_frac", "res_frac", "fissure", "cond_fault", "res_fault"];

export default function ImageLogScreen({
  project,
  well,
  onDataChanged,
}: {
  project: Project;
  well: Well;
  onDataChanged?: () => void;
}) {
  const toast = useToast();
  const mayWrite = canWrite(project.my_role);
  const mayCompute = canCompute(project.my_role);

  const [logId, setLogId] = useState<number | null>(null);
  const [top, setTop] = useState(0);
  // 2,5 m, bukan enam. Perlapisan yang miring 10° pada lubang 8½" menggambar
  // sinusoid setinggi dua sentimeter: pada jendela enam meter itu 0,3% tinggi
  // panel — sebuah garis lurus, dan lengkung yang justru menjadi seluruh isi
  // pembacaan citra tidak terlihat sama sekali sampai pembacanya memperbesar.
  const [span, setSpan] = useStored("evolog.image.span", 2.5);
  const [ramp, setRamp] = useStored("evolog.image.ramp", "fmi");
  // Kontras citra: rentang bita yang direntang ke seluruh ramp. Kosong berarti
  // seluruh 0–255, seperti sebelum setelan ini ada.
  const [stretch, setStretch] = useStored<ByteStretch>("evolog.image.stretch", {
    min: null,
    max: null,
  });
  const [rail, setRail] = useStored<Rail>("evolog.image.rail", "pick");
  const [view, setView] = useStored<"both" | "static" | "dynamic">("evolog.image.view", "both");
  const [showCylinder, setShowCylinder] = useStored("evolog.image.cylinder", true);
  const [showSinusoids, setShowSinusoids] = useStored("evolog.image.sinusoids", true);
  const [viewAzimuth, setViewAzimuth] = useState(0);
  const [window_, setWindow] = useStored("evolog.image.window", 1);

  const [picks, setPicks] = useState<PickPoint[]>([]);
  const [fit, setFit] = useState<SinusoidFit | null>(null);
  const [kind, setKind] = useStored("evolog.image.kind", "bed");
  const [aperture, setAperture] = useState<number | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [kindFilter, setKindFilter] = useStored<string[]>("evolog.image.filter", []);
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0);

  /* -------------------------------------------------------------- */

  const logs = useAsync(
    (signal) => api
      .get<Page<ImageLog>>("/api/image-logs/", { well: well.id }, signal)
      .then((page) => unpage(page, signal)),
    [well.id, version],
  );

  const log = useMemo(
    () => logs.data?.find((row) => row.id === logId) ?? logs.data?.[0] ?? null,
    [logs.data, logId],
  );

  // Jendela pertama dipasang begitu lintasannya diketahui, dan hanya sekali per
  // lintasan: menyetelnya ulang tiap render akan melempar pembaca kembali ke
  // puncak lubang tiap kali sebuah bidang disimpan.
  const settled = useRef<number | null>(null);
  useEffect(() => {
    if (!log || settled.current === log.id) return;
    settled.current = log.id;
    setLogId(log.id);
    setTop(log.depth_start);
    setPicks([]);
    setFit(null);
  }, [log]);

  const base = log ? Math.min(top + span, log.depth_stop) : top + span;

  const win = useAsync(
    (signal) => {
      if (!log) return Promise.resolve(null);
      return api.get<import("../lib/imagelog").ImageWindow>(
        `/api/image-logs/${log.id}/window/`,
        { top, base, norm: "both" },
        signal,
      );
    },
    [log?.id, top, base],
  );

  const features = useAsync(
    (signal) => {
      if (!log) return Promise.resolve([] as Feature[]);
      return api
        .get<Page<Feature>>("/api/image-features/", { image_log: log.id }, signal)
        .then((page) => unpage(page, signal));
    },
    [log?.id, version],
  );

  const breakouts = useAsync(
    (signal) => {
      if (!log) return Promise.resolve([] as Breakout[]);
      return api
        .get<Page<Breakout>>("/api/breakouts/", { image_log: log.id }, signal)
        .then((page) => unpage(page, signal));
    },
    [log?.id, version],
  );

  const stats = useAsync(
    (signal) => {
      if (!log) return Promise.resolve(null);
      return api.get<ImageStatistics>(`/api/image-logs/${log.id}/statistics/`, {}, signal);
    },
    [log?.id, version],
  );

  const damage = useAsync(
    (signal) => api
      .get<Page<DamageZone>>("/api/damage-zones/", { well: well.id }, signal)
      .then((page) => unpage(page, signal)),
    [well.id, version],
  );

  const descriptions = useAsync(
    (signal) => api
      .get<Page<CoreDescription>>("/api/core-descriptions/", { well: well.id }, signal)
      .then((page) => unpage(page, signal)),
    [well.id, version],
  );

  const documents = useWellDocuments(well.id);

  const runs = useAsync(
    (signal) => {
      if (!log) return Promise.resolve([] as ImageRun[]);
      return api
        .get<Page<ImageRun>>("/api/image-runs/", { image_log: log.id }, signal)
        .then((page) => unpage(page, signal));
    },
    [log?.id, version],
  );

  /* -------------------------------------------------------------- */

  const shown = useMemo(() => {
    const rows = features.data ?? [];
    const filtered = kindFilter.length
      ? rows.filter((row) => kindFilter.includes(row.kind))
      : rows;
    return filtered.filter((row) => row.md >= top - span && row.md <= base + span);
  }, [features.data, kindFilter, top, base, span]);

  const fractures = useMemo(
    () => (features.data ?? []).filter((row) => FRACTURE_KINDS.includes(row.kind)),
    [features.data],
  );

  const running = useMemo(
    () => runningStats(fractures, top, base, window_, Math.max(span / 220, 0.02)),
    [fractures, top, base, window_, span],
  );

  const ghost = useMemo(() => {
    const preview = previewFit(picks.map((p) => [p.azimuth, p.md] as [number, number]));
    if (!preview || !log) return null;
    return {
      md: preview.md,
      apparent_dip: (Math.atan2(preview.amplitude, log.hole_diameter / 2) * 180) / Math.PI,
      apparent_azimuth: preview.azimuth,
    };
  }, [picks, log]);

  // Pemasangan di server adalah yang tersimpan; yang di browser hanya bayangan
  // sementara. Keduanya memakai bentuk tertutup yang sama, jadi angkanya tidak
  // melompat ketika jawabannya tiba.
  useEffect(() => {
    if (!log || picks.length < 3) {
      setFit(null);
      return;
    }
    let alive = true;
    api
      .post<SinusoidFit>(`/api/image-logs/${log.id}/fit/`, {
        picks: picks.map((p) => [p.azimuth, p.md]),
      })
      .then((result) => alive && setFit(result))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [picks, log]);

  const savePick = async () => {
    if (!log || !fit) return;
    setBusy(true);
    try {
      await api.post("/api/image-features/", {
        image_log: log.id,
        kind,
        md: fit.md,
        dip: fit.dip,
        azimuth: fit.azimuth,
        apparent_dip: fit.apparent_dip,
        apparent_azimuth: fit.apparent_azimuth,
        amplitude: fit.amplitude,
        aperture_mm: aperture,
        picks: picks.map((p) => [p.azimuth, p.md]),
      });
      setPicks([]);
      setFit(null);
      setAperture(null);
      setVersion((v) => v + 1);
      toast("Plane saved.");
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setBusy(false);
    }
  };

  const removeFeature = async (id: number) => {
    try {
      await api.delete(`/api/image-features/${id}/`);
      setSelected(null);
      setVersion((v) => v + 1);
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    }
  };

  const publish = async () => {
    if (!log) return;
    setBusy(true);
    try {
      const run = await api.post<ImageRun>(`/api/image-logs/${log.id}/compute/`, {
        window: window_,
        step: 0.1524,
        top: log.depth_start,
        base: log.depth_stop,
      });
      toast(`${count(run.summary.fractures ?? 0, "fracture")} published as curves.`);
      setVersion((v) => v + 1);
      onDataChanged?.();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setBusy(false);
    }
  };

  /* -------------------------------------------------------------- */

  if (logs.loading && !logs.data) return <Loading label="Reading the image…" />;
  if (!logs.data?.length) {
    // Sumur tua tanpa citra digital sering masih punya scan dipmeter; itu
    // bukan citra, tetapi tetap jawaban atas pertanyaan yang membawa orang ke
    // layar ini.
    if (documents.data?.length) {
      return (
        <div style={{ padding: 12, maxWidth: 640, overflow: "auto", height: "100%" }}>
          <Empty>{well.name} has no digital borehole image — only scanned documents.</Empty>
          <WellDocuments documents={documents.data} />
        </div>
      );
    }
    return (
      <Empty>
        {well.name} has no borehole image yet.
        {mayWrite && (
          <span style={{ font: "400 11.5px var(--font-sans)", color: "var(--ink-5)" }}>
            Load one from <b>Image &amp; Core → Import image log…</b>, or generate
            sample data with <code>manage.py seed_borehole</code>.
          </span>
        )}
      </Empty>
    );
  }

  const sheetHeight = 620;
  const imageWidth = view === "both" ? 190 : 300;
  const scale = { top, base, height: sheetHeight };

  return (
    <div style={{ display: "flex", height: "100%", minHeight: 0 }}>
      {/* ---------------- sheet ---------------- */}
      <div style={{ flex: 1, minWidth: 0, overflow: "auto", padding: "10px 12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <Dropdown
            value={String(log?.id ?? "")}
            options={(logs.data ?? []).map((row) => ({
              value: String(row.id),
              label: `${row.name} · ${row.tool}`,
            }))}
            onChange={(value) => {
              settled.current = null;
              setLogId(Number(value));
            }}
          />
          <Segmented
            value={view}
            options={[
              { value: "both", label: "Static + dynamic" },
              { value: "static", label: "Static" },
              { value: "dynamic", label: "Dynamic" },
            ]}
            onChange={setView}
            size="sm"
          />
          <span style={{ font: "400 10.5px var(--font-mono)", color: "var(--ink-5)" }}>
            {top.toFixed(1)}–{base.toFixed(1)} m · {log?.bins} bin · {log?.pads} pad
          </span>
          <span style={{ flex: 1 }} />
          <Button
            variant="ghost"
            onClick={() => setTop(Math.max((log?.depth_start ?? 0), top - span * 0.8))}
            title="Move the window up"
          >
            ↑
          </Button>
          <Button
            variant="ghost"
            onClick={() =>
              setTop(Math.min((log?.depth_stop ?? 0) - span, top + span * 0.8))
            }
            title="Move the window down"
          >
            ↓
          </Button>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <DepthGutter top={top} base={base} height={sheetHeight} />

          {(view === "both" || view === "static") && (
            <div>
              <AzimuthRuler width={imageWidth} />
              <ImageCanvas
                win={win.data ?? null}
                which="static"
                ramp={ramp}
                stretch={stretch}
                features={shown}
                holeDiameter={log?.hole_diameter ?? 0.2159}
                breakouts={breakouts.data ?? []}
                picks={picks}
                ghost={ghost}
                selectedId={selected}
                showSinusoids={showSinusoids}
                width={imageWidth}
                height={sheetHeight}
                onPick={mayWrite && rail === "pick"
                  ? (point) => setPicks((rows) => [...rows, point])
                  : undefined}
                onSelect={(feature) => setSelected(feature.id)}
                label="Static"
              />
            </div>
          )}

          {(view === "both" || view === "dynamic") && (
            <div>
              <AzimuthRuler width={imageWidth} />
              <ImageCanvas
                win={win.data ?? null}
                which="dynamic"
                ramp={ramp}
                stretch={stretch}
                features={shown}
                holeDiameter={log?.hole_diameter ?? 0.2159}
                breakouts={breakouts.data ?? []}
                picks={picks}
                ghost={ghost}
                selectedId={selected}
                showSinusoids={showSinusoids}
                width={imageWidth}
                height={sheetHeight}
                onPick={mayWrite && rail === "pick"
                  ? (point) => setPicks((rows) => [...rows, point])
                  : undefined}
                onSelect={(feature) => setSelected(feature.id)}
                label="Dynamic"
              />
            </div>
          )}

          {showCylinder && (
            <div style={{ paddingTop: 14 }}>
              <BoreholeCylinder
                win={win.data ?? null}
                which="dynamic"
                ramp={ramp}
                stretch={stretch}
                viewAzimuth={viewAzimuth}
                onRotate={setViewAzimuth}
                width={120}
                height={sheetHeight}
                features={showSinusoids ? shown : []}
                holeDiameter={log?.hole_diameter ?? 0.2159}
              />
              <div style={{
                textAlign: "center",
                font: "400 8.5px var(--font-mono)",
                color: "var(--ink-6)",
                width: 120,
              }}>
                menghadap {bearing(viewAzimuth)}°
              </div>
            </div>
          )}

          <div style={{ paddingTop: 14, display: "flex", gap: 3 }}>
            <TadpoleTrack
              features={shown}
              width={92}
              scale={scale}
              onSelect={(feature) => setSelected(feature.id)}
              selectedId={selected}
            />
            <SpikeTrack
              points={fractures
                .filter((row) => row.aperture_mm != null)
                .map((row) => ({ md: row.md, value: row.aperture_mm as number, color: row.color }))}
              running={running.map((row) => ({ md: row.md, value: row.aperture }))}
              width={62}
              scale={scale}
              title="Aperture"
              unit="mm"
              max={1.5}
              color="#8b1a1a"
            />
            <SpikeTrack
              points={[]}
              running={running.map((row) => ({
                md: row.md,
                value: (row.aperture / 1000) * row.density,
              }))}
              width={62}
              scale={scale}
              title="Frac. φ"
              unit="v/v"
              max={0.005}
              color="#7d3c98"
            />
            <SpikeTrack
              points={fractures.map((row) => ({ md: row.md, value: 1, color: row.color }))}
              running={running.map((row) => ({ md: row.md, value: row.density }))}
              width={62}
              scale={scale}
              title="Frac. density"
              unit="1/m"
              max={Math.max(4, Math.ceil(Math.max(...running.map((r) => r.density), 1)))}
              color="#d1342f"
            />
            <IntervalColumn
              intervals={(descriptions.data ?? []).map((row) => ({
                top_md: row.top_md,
                base_md: row.base_md,
                color: row.color,
                label: row.lithology.slice(0, 9),
                hint: `${row.lithology} — ${row.grain_size} ${row.structure}`,
              }))}
              width={70}
              scale={scale}
              title="Lithology"
            />
            <IntervalColumn
              intervals={(damage.data ?? []).map((row) => ({
                top_md: row.top_md,
                base_md: row.base_md,
                color: DAMAGE_COLORS[row.grade] ?? "#ccc",
                label: row.fault_zone || row.grade_label,
                hint: `${row.grade_label}${row.fault_zone ? ` — ${row.fault_zone}` : ""}`,
              }))}
              width={78}
              scale={scale}
              title="Damage zone"
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: 20, alignItems: "center", marginTop: 12 }}>
          {/* Lebarnya cukup untuk memuat nama dan angkanya pada satu baris.
              Pada 180 px, "Window height" membungkus menjadi dua baris dan
              angkanya terlempar ke bawah pelurunya. */}
          <div style={{ width: 250, flex: "none" }}>
            <Slider label="Window height" value={span} min={0.5} max={40} step={0.5}
                    onChange={setSpan} format={(v) => `${v.toFixed(1)} m`} />
          </div>
          <div style={{ width: 320, flex: "none" }}>
            <Slider
              label="Top"
              value={top}
              min={log?.depth_start ?? 0}
              max={Math.max((log?.depth_stop ?? 1) - span, log?.depth_start ?? 0)}
              step={span / 10}
              onChange={setTop}
              format={(v) => `${v.toFixed(1)} m`}
            />
          </div>
          {/* `Toggle` melebar sampai selebar induknya; tanpa kotak berlebar
              tetap, sakelarnya terlempar ke ujung kanan layar dan namanya
              tertinggal sendirian di sebelah kiri. */}
          <div style={{ width: 130, flex: "none" }}>
            <Toggle label="Sinusoid" checked={showSinusoids} onChange={setShowSinusoids} />
          </div>
          <div style={{ width: 140, flex: "none" }}>
            <Toggle label="3D cylinder" checked={showCylinder} onChange={setShowCylinder} />
          </div>
        </div>
      </div>

      {/* ---------------- rail ---------------- */}
      <aside
        style={{
          width: 316,
          flex: "none",
          borderLeft: "1px solid var(--line)",
          background: "var(--panel)",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <div style={{ padding: "8px 8px 6px", borderBottom: "1px solid var(--line)" }}>
          <Segmented
            value={rail}
            options={[
              { value: "pick", label: "Pick" },
              { value: "features", label: "Planes" },
              { value: "stats", label: "Dips" },
              { value: "stress", label: "Stress" },
              { value: "process", label: "Publish" },
              ...(documents.data?.length ? [{ value: "scans" as Rail, label: "Scans" }] : []),
            ]}
            onChange={setRail}
            size="sm"
          />
        </div>

        <div className="scroll-y" style={{ flex: 1, overflow: "auto", padding: 8 }}>
          {rail === "pick" && (
            <PickPanel
              mayWrite={mayWrite}
              picks={picks}
              fit={fit}
              kind={kind}
              onKind={setKind}
              aperture={aperture}
              onAperture={setAperture}
              onUndo={() => setPicks((rows) => rows.slice(0, -1))}
              onClear={() => setPicks([])}
              onSave={savePick}
              busy={busy}
              ramp={ramp}
              onRamp={setRamp}
              stretch={stretch}
              onStretch={setStretch}
            />
          )}

          {rail === "features" && (
            <FeaturePanel
              features={features.data ?? []}
              kindFilter={kindFilter}
              onKindFilter={setKindFilter}
              selected={selected}
              onSelect={(feature) => {
                setSelected(feature.id);
                setTop(Math.max((log?.depth_start ?? 0), feature.md - span / 2));
              }}
              onDelete={mayWrite ? removeFeature : undefined}
            />
          )}

          {rail === "stats" && (
            <StatsPanel stats={stats.data ?? null} features={features.data ?? []}
                        top={log?.depth_start ?? 0} base={log?.depth_stop ?? 1} />
          )}

          {rail === "stress" && <StressPanel stats={stats.data ?? null} />}

          {rail === "scans" && <WellDocuments documents={documents.data ?? []} />}

          {rail === "process" && (
            <ProcessPanel
              mayCompute={mayCompute}
              window={window_}
              onWindow={setWindow}
              fractures={fractures.length}
              runs={runs.data ?? []}
              busy={busy}
              onPublish={publish}
            />
          )}
        </div>
      </aside>
    </div>
  );
}

const DAMAGE_COLORS: Record<string, string> = {
  host: "#e8e4d9",
  damage: "#f5a623",
  core: "#e91e8c",
  healed: "#2e7d32",
};

/* ------------------------------------------------------------------ */

function DepthGutter({ top, base, height }: { top: number; base: number; height: number }) {
  const span = base - top || 1;
  const step = niceStep(span);
  const marks: number[] = [];
  for (let md = Math.ceil(top / step) * step; md <= base; md += step) marks.push(md);
  return (
    <div style={{ paddingTop: 14, flex: "none" }}>
      <svg viewBox={`0 0 46 ${height}`} width={46} height={height}>
        {marks.map((md) => {
          const y = ((md - top) / span) * height;
          return (
            <g key={md}>
              <line x1={34} y1={y} x2={46} y2={y} stroke="var(--line-strong)" strokeWidth={0.7} />
              {/* Dasar tulisan dijepit ke dalam bingkai. Tanpa itu label paling
                  atas tercetak separuh di luar viewBox dan terbaca sebagai
                  angka yang terpotong — persis pada label yang menyebutkan
                  puncak jendela. */}
              <text x={31} y={Math.min(Math.max(y + 3, 9), height - 2)} textAnchor="end"
                    style={{ font: "400 9px var(--font-mono)", fill: "var(--ink-5)" }}>
                {md.toFixed(step < 1 ? 1 : 0)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/** A 1-2-5 step that puts roughly ten labels in the window. */
function niceStep(span: number): number {
  const target = span / 10;
  const power = Math.pow(10, Math.floor(Math.log10(Math.max(target, 1e-6))));
  for (const factor of [1, 2, 5, 10]) {
    if (power * factor >= target) return power * factor;
  }
  return power * 10;
}

function PickPanel({
  mayWrite, picks, fit, kind, onKind, aperture, onAperture,
  onUndo, onClear, onSave, busy, ramp, onRamp, stretch, onStretch,
}: {
  mayWrite: boolean;
  picks: PickPoint[];
  fit: SinusoidFit | null;
  kind: string;
  onKind: (value: string) => void;
  aperture: number | null;
  onAperture: (value: number | null) => void;
  onUndo: () => void;
  onClear: () => void;
  onSave: () => void;
  busy: boolean;
  ramp: string;
  onRamp: (value: string) => void;
  stretch: ByteStretch;
  onStretch: (value: ByteStretch) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <Panel title="Colour">
        <Dropdown
          value={ramp}
          options={rampKeys().map((key) => ({ value: key, label: rampLabel(key) }))}
          onChange={onRamp}
        />
        <div style={{
          height: 12,
          borderRadius: 3,
          border: "1px solid var(--line)",
          background: rampGradient(ramp),
        }} />
        <EditColoursButton ramp={ramp} onRamp={onRamp} />
        {/* Nilai citra adalah bita 0–255, bukan satuan fisik: yang disetel di
            sini kontrasnya — bagian rentang itu yang memakai seluruh ramp. */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <OptionalNumberField
            label="Minimum"
            value={stretch.min}
            step={1}
            min={0}
            max={255}
            placeholder="0"
            onChange={(min) => onStretch({ ...stretch, min })}
          />
          <OptionalNumberField
            label="Maximum"
            value={stretch.max}
            step={1}
            min={0}
            max={255}
            placeholder="255"
            onChange={(max) => onStretch({ ...stretch, max })}
          />
        </div>
      </Panel>

      <Panel title="Pick a plane" meta={count(picks.length, "point")}>
        {!mayWrite && (
          <Notice tone="info">
            Your role is read-only. A sinusoid may still be fitted to measure with, but
            it cannot be saved.
          </Notice>
        )}
        <span style={{ font: "400 11px var(--font-sans)", color: "var(--ink-4)" }}>
          Click at least three points along the feature on the image. Two points describe
          infinitely many planes, so the dip only appears at the third.
        </span>
        <div style={{ display: "flex", gap: 6 }}>
          <Button variant="ghost" onClick={onUndo} disabled={!picks.length}>Undo</Button>
          <Button variant="ghost" onClick={onClear} disabled={!picks.length}>Clear</Button>
        </div>

        {fit ? (
          <>
            <KeyValue k="Depth" v={`${fit.md.toFixed(2)} m`} />
            <KeyValue k="Apparent dip" v={`${fit.apparent_dip.toFixed(1)}°`} />
            <KeyValue k="True dip" v={`${fit.dip.toFixed(1)}°`} />
            <KeyValue k="Dip azimuth" v={`${bearing(fit.azimuth)}°`} />
            <KeyValue k="Hole" v={`${fit.deviation.toFixed(1)}° / ${bearing(fit.hole_azimuth)}°`} />
            <KeyValue k="RMS residual" v={`${(fit.rms * 100).toFixed(1)} cm`} />
          </>
        ) : (
          <span style={{ font: "400 10.5px var(--font-mono)", color: "var(--ink-6)" }}>
            {picks.length < 3 ? `${count(3 - picks.length, "point")} to go` : "Fitting…"}
          </span>
        )}
      </Panel>

      <Panel title="Save as">
        <Dropdown value={kind} options={KINDS} onChange={onKind} />
        {/* Boleh kosong, dan kosong berarti sesuatu: bukaan yang tidak
            diukur bukan bukaan nol, dan porositas rekahan yang dihitung dari
            nol akan melaporkan rekahan yang rapat sempurna. */}
        <OptionalNumberField
          label="Aperture"
          unit="mm"
          value={aperture}
          onChange={onAperture}
          step={0.01}
        />
        <Button variant="primary" busy={busy} disabled={!fit || !mayWrite} onClick={onSave}>
          Save the plane
        </Button>
      </Panel>
    </div>
  );
}

function FeaturePanel({
  features, kindFilter, onKindFilter, selected, onSelect, onDelete,
}: {
  features: Feature[];
  kindFilter: string[];
  onKindFilter: (value: string[]) => void;
  selected: number | null;
  onSelect: (feature: Feature) => void;
  onDelete?: (id: number) => void;
}) {
  const counts = useMemo(() => {
    const out: Record<string, number> = {};
    for (const feature of features) out[feature.kind] = (out[feature.kind] ?? 0) + 1;
    return out;
  }, [features]);

  const shown = kindFilter.length
    ? features.filter((row) => kindFilter.includes(row.kind))
    : features;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <Panel title="Kind" meta={count(features.length, "plane")}>
        {KINDS.filter((option) => counts[option.value]).map((option) => {
          const on = kindFilter.includes(option.value);
          return (
            <button
              key={option.value}
              type="button"
              onClick={() =>
                onKindFilter(
                  on
                    ? kindFilter.filter((value) => value !== option.value)
                    : [...kindFilter, option.value],
                )
              }
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                background: on ? "var(--selected)" : "transparent",
                border: "none",
                borderRadius: 4,
                padding: "3px 5px",
                cursor: "pointer",
                textAlign: "left",
                width: "100%",
              }}
            >
              <span style={{
                width: 9, height: 9, borderRadius: 2, flex: "none",
                background: FEATURE_COLORS[option.value] ?? "#888",
              }} />
              <span style={{ font: "400 11px var(--font-sans)", color: "var(--ink-3)", flex: 1 }}>
                {option.label}
              </span>
              <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
                {counts[option.value]}
              </span>
            </button>
          );
        })}
        {kindFilter.length > 0 && (
          <Button variant="ghost" onClick={() => onKindFilter([])}>All kinds</Button>
        )}
      </Panel>

      <Panel title="List" meta={`${shown.length}`} flush>
        <div style={{ maxHeight: 420, overflow: "auto" }}>
          {shown.length === 0 && <Empty>No plane matches yet.</Empty>}
          {shown.map((feature) => (
            <div
              key={feature.id}
              onClick={() => onSelect(feature)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                padding: "4px 7px",
                cursor: "pointer",
                background: feature.id === selected ? "var(--selected)" : undefined,
                borderBottom: "1px solid var(--line-soft)",
              }}
            >
              <span style={{
                width: 8, height: 8, borderRadius: 2, flex: "none", background: feature.color,
              }} />
              <span style={{ font: "400 10.5px var(--font-mono)", color: "var(--ink-3)", width: 66 }}>
                {feature.md.toFixed(2)}
              </span>
              <span style={{ font: "400 10.5px var(--font-mono)", color: "var(--ink-4)", flex: 1 }}>
                {feature.dip.toFixed(0)}° / {bearing(feature.azimuth)}°
              </span>
              {onDelete && (
                <button
                  type="button"
                  title="Delete this plane"
                  onClick={(event) => {
                    event.stopPropagation();
                    onDelete(feature.id);
                  }}
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    color: "var(--ink-6)", padding: 0,
                  }}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

const FEATURE_COLORS: Record<string, string> = {
  bed: "#2f6fd0",
  cross_bed: "#5aa9e6",
  lamination: "#8fc7ef",
  cond_frac: "#d1342f",
  res_frac: "#2e8b57",
  fissure: "#b8860b",
  induced_frac: "#e07b39",
  cond_fault: "#8b1a1a",
  res_fault: "#145a32",
  vug: "#7d3c98",
  nodule: "#5d6d7e",
  breakout: "#c2185b",
};

function StatsPanel({
  stats, features, top, base,
}: {
  stats: ImageStatistics | null;
  features: Feature[];
  top: number;
  base: number;
}) {
  if (!stats) return <Loading />;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <Panel title="Bedding" meta={`${stats.bedding.count}`}>
        <RoseDiagram
          size={190}
          sets={[{ rose: stats.bedding.rose, color: "#2f6fd0", label: "bedding" }]}
          markers={stats.bedding.vector.mean != null
            ? [{ azimuth: stats.bedding.vector.mean, color: "#0b3d91", label: "Mean dip azimuth" }]
            : undefined}
          caption="Dip azimuth"
        />
        <MeanLine vector={stats.bedding.vector} />
      </Panel>

      <Panel title="Fractures" meta={`${stats.fractures.count}`}>
        <RoseDiagram
          size={190}
          sets={[{ rose: stats.fractures.rose, color: "#d1342f", label: "fractures" }]}
          markers={stats.fractures.vector.mean != null
            ? [{ azimuth: stats.fractures.vector.mean, color: "#8b1a1a", label: "Mean strike" }]
            : undefined}
          caption="Dip azimuth"
        />
        <MeanLine vector={stats.fractures.vector} />
      </Panel>

      <Panel title="Dip vs depth">
        <DipScatter
          features={features.map((row) => ({
            md: row.md, dip: row.dip, color: row.color, kind_label: row.kind_label,
          }))}
          top={top}
          base={base}
        />
      </Panel>

      <Panel title="By kind">
        {Object.entries(stats.by_kind).map(([key, value]) => (
          <KeyValue
            key={key}
            k={value.label}
            v={`${value.count} · ${value.mean_dip != null ? `${value.mean_dip.toFixed(0)}°` : "—"}`}
          />
        ))}
      </Panel>
    </div>
  );
}

function MeanLine({ vector }: { vector: { mean: number | null; resultant: number; count: number } }) {
  return (
    <>
      <KeyValue k="Mean direction" v={vector.mean != null ? `${bearing(vector.mean)}°` : "—"} />
      {/* R disebut apa adanya, dan disebut artinya. Sebuah "arah rata-rata"
          yang dicetak tanpa keterangan bahwa sebarannya hampir merata adalah
          angka yang akan dikutip seolah ia berarti sesuatu. */}
      <KeyValue k="Resultant R" v={vector.resultant.toFixed(2)} />
      {vector.count > 0 && vector.resultant < 0.25 && (
        <Notice tone="warn">
          R is low — the azimuths are spread almost evenly, so the mean direction above
          means almost nothing.
        </Notice>
      )}
    </>
  );
}

function StressPanel({ stats }: { stats: ImageStatistics | null }) {
  if (!stats) return <Loading />;
  const stress = stats.stress;
  const shmax = stress.shmax;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <Panel title="Stress orientation" meta={count(stress.count, "indicator")}>
        {stress.count === 0 ? (
          <Empty>No breakout or induced fracture has been mapped yet.</Empty>
        ) : (
          <>
            <RoseDiagram
              size={210}
              sets={[
                { rose: stress.breakout_rose, color: "#c2185b", label: "breakout" },
                { rose: stress.ditf_rose, color: "#f2c200", label: "induced", opacity: 0.85 },
              ]}
              markers={shmax != null
                ? [
                    { azimuth: shmax, color: "#111", label: "SHmax", period: 180 },
                    {
                      azimuth: (shmax + 90) % 180,
                      color: "#777",
                      label: "Shmin",
                      dashed: true,
                      period: 180,
                    },
                  ]
                : undefined}
              caption="Breakout purple · induced fracture yellow"
            />
            <KeyValue k="SHmax" v={shmax != null ? `${bearing(shmax, 180)}°` : "—"} />
            <KeyValue k="Shmin" v={shmax != null ? `${bearing(shmax + 90, 180)}°` : "—"} />
            <KeyValue k="Breakout" v={`${stress.breakout_count} · ${stress.length.toFixed(1)} m`} />
            <KeyValue k="Induced fractures" v={String(stress.ditf_count)} />
            <KeyValue k="Resultant R" v={stress.resultant.toFixed(2)} />
            {stress.resultant < 0.5 && (
              <Notice tone="warn">
                The stress indicators disagree. At an R this low the SHmax direction above
                is not yet worth quoting.
              </Notice>
            )}
          </>
        )}
      </Panel>
      <Panel title="How to read it">
        <span style={{ font: "400 11px/1.6 var(--font-sans)", color: "var(--ink-4)" }}>
          Breakouts fail where the hoop stress is greatest, that is 90° from SHmax;
          drilling-induced tensile fractures open along SHmax instead. The two are combined
          by length — one breakout twelve metres long is evidence of a different weight
          than three hand-span pieces.
        </span>
      </Panel>
    </div>
  );
}

function ProcessPanel({
  mayCompute, window: windowM, onWindow, fractures, runs, busy, onPublish,
}: {
  mayCompute: boolean;
  window: number;
  onWindow: (value: number) => void;
  fractures: number;
  runs: ImageRun[];
  busy: boolean;
  onPublish: () => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <Panel title="Fracture curves">
        <span style={{ font: "400 11px/1.6 var(--font-sans)", color: "var(--ink-4)" }}>
          Fracture density, aperture and porosity are published as an ordinary curve set
          (<code>FRAC_DENS</code>, <code>FRAC_APER</code>, <code>FRAC_PHI</code>) — after that
          they can be plotted on the log sheet, the crossplot, the correlation panel and the
          CPI sheet with no special handling.
        </span>
        <KeyValue k="Fractures mapped" v={String(fractures)} />
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ font: "400 10.5px var(--font-mono)", color: "var(--ink-5)", width: 96 }}>
            Window {windowM.toFixed(2)} m
          </span>
          <div style={{ flex: 1 }}>
            <Slider label="Window" value={windowM} min={0.25} max={5} step={0.25}
              onChange={onWindow} format={(v) => `${v.toFixed(2)} m`} />
          </div>
        </div>
        <Button
          variant="primary"
          busy={busy}
          disabled={!mayCompute || fractures === 0}
          onClick={onPublish}
        >
          Publish the curves
        </Button>
        {!mayCompute && (
          <Notice tone="info">Only owners and petrophysicists may compute.</Notice>
        )}
      </Panel>

      <Panel title="History" meta={`${runs.length}`}>
        {runs.length === 0 && <Empty>Never run yet.</Empty>}
        {runs.map((run) => (
          <div key={run.id} style={{ borderBottom: "1px solid var(--line-soft)", padding: "4px 0" }}>
            <KeyValue
              k={new Date(run.created_at).toLocaleString("en-GB")}
              v={count(run.summary.fractures ?? 0, "fracture")}
            />
            <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
              window {String((run.parameters as { window?: number }).window ?? "—")} m ·
              {" "}max {run.summary.max_density?.toFixed(1) ?? "—"} 1/m ·
              {" "}{run.duration_ms} ms
            </span>
          </div>
        ))}
      </Panel>
    </div>
  );
}
